import React, { useEffect, useMemo, useState } from 'react';
import { Users, Building2, Ticket, CreditCard, ArrowRight, Gauge, Sparkles } from 'lucide-react';
import { backofficeApiFetch } from '../utils/backofficeApi';

const StatCard = ({ title, value, sub, icon: Icon, gradientFrom, gradientTo, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left rounded-2xl p-5 bg-gradient-to-br ${gradientFrom} ${gradientTo} text-white shadow hover:shadow-lg transition`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm/5 opacity-90">{title}</div>
        <div className="mt-1 text-4xl font-extrabold tracking-tight">{value}</div>
        {sub && <div className="mt-1 text-xs/5 opacity-90">{sub}</div>}
      </div>
      {Icon && (
        <div className="p-2 rounded-xl bg-white/15">
          <Icon className="w-6 h-6" />
        </div>
      )}
    </div>
  </button>
);

const AdminDashboard = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);

  const [usersCount, setUsersCount] = useState(null);
  const [companiesCount, setCompaniesCount] = useState(null);
  const [plans, setPlans] = useState({ basic: 0, pro: 0, max: 0 });
  const [subscriptionsCount, setSubscriptionsCount] = useState(null);
  const [openTickets, setOpenTickets] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (isMounted) setLoading(true);

      try {
        const summary = await backofficeApiFetch('/v1/dashboard/summary');
        if (!isMounted) return;
        setUsersCount(summary.usersCount ?? '—');
        setCompaniesCount(summary.companiesCount ?? '—');
        setPlans(summary.plans || { basic: 0, pro: 0, max: 0 });
        setSubscriptionsCount(summary.subscriptionsCount ?? '—');
        setOpenTickets(summary.openTickets ?? '—');
        setErrors([]);
      } catch (e) {
        if (isMounted) {
          setUsersCount('—');
          setCompaniesCount('—');
          setPlans({ basic: '—', pro: '—', max: '—' });
          setSubscriptionsCount('—');
          setOpenTickets('—');
          setErrors([e.message || 'Dashboard API unavailable']);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    const refreshInterval = setInterval(load, 30000);
    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, []);

  const planSummary = useMemo(() => {
    if (plans.basic === '—') return '—';
    return `Basic ${plans.basic} · Pro ${plans.pro} · Max ${plans.max}`;
  }, [plans]);

  const safeRatio = (value, total) => {
    const safeValue = Number(value);
    const safeTotal = Number(total);
    if (!Number.isFinite(safeValue) || !Number.isFinite(safeTotal) || safeTotal <= 0) {
      return '0%';
    }
    return `${Math.min((safeValue / safeTotal) * 100, 100)}%`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-gray-900">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h2 className="text-2xl font-bold">Backoffice Dashboard</h2>
        </div>
        <div className="text-sm text-gray-500">Quick overview across platforms</div>
        {loading && <div className="mt-2 text-xs text-gray-400">Loading summary…</div>}
        {errors.length > 0 && (
          <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            {errors.join(' | ')}
            <p className="mt-1 text-gray-600">
              Deploy backoffice functions so <code>/v1/dashboard/summary</code> is available, then hard-refresh.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={usersCount === null ? '…' : usersCount}
          sub="Registered in App"
          icon={Users}
          gradientFrom="from-blue-600"
          gradientTo="to-indigo-600"
          onClick={() => onNavigate?.('users')}
        />
        <StatCard
          title="Companies"
          value={companiesCount === null ? '…' : companiesCount}
          sub={planSummary}
          icon={Building2}
          gradientFrom="from-purple-600"
          gradientTo="to-violet-600"
          onClick={() => onNavigate?.('companies')}
        />
        <StatCard
          title="Subscriptions"
          value={subscriptionsCount === null ? '…' : subscriptionsCount}
          sub="Active subscriptions"
          icon={CreditCard}
          gradientFrom="from-emerald-600"
          gradientTo="to-teal-600"
          onClick={() => onNavigate?.('subscription-manager')}
        />
        <StatCard
          title="Open Tickets"
          value={openTickets === null ? '…' : openTickets}
          sub="Support queue"
          icon={Ticket}
          gradientFrom="from-orange-600"
          gradientTo="to-rose-600"
          onClick={() => onNavigate?.('logs')}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-4 h-4 text-purple-600" />
            <div className="text-sm font-semibold text-gray-900">Plan Breakdown</div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Basic / Starter</span>
                <span>{plans.basic ?? '…'}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded">
                <div className="h-2 rounded bg-blue-500" style={{ width: safeRatio(plans.basic, companiesCount) }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Pro</span>
                <span>{plans.pro ?? '…'}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded">
                <div className="h-2 rounded bg-violet-500" style={{ width: safeRatio(plans.pro, companiesCount) }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Max / Enterprise</span>
                <span>{plans.max ?? '…'}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded">
                <div className="h-2 rounded bg-rose-500" style={{ width: safeRatio(plans.max, companiesCount) }} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-5">
          <div className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</div>
          <div className="space-y-2">
            <button
              onClick={() => onNavigate?.('subscription-manager')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              <span>Subscription Manager</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigate?.('users')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            >
              <span>Manage Users</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigate?.('public-content')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100"
            >
              <span>Edit Public Content</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigate?.('logs')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100"
            >
              <span>View Logs</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
